import { type DocumentReference } from 'firebase/firestore';

import { getHostingBucket } from 'services/Firebase';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentType } from 'types/DocumentType';
import { ProcessingStatus } from 'types/ProcessingStatus';
import { formatDateTimeLongMinutes } from 'utils/Dates';
import { nullTimestamp } from 'utils/FirestoreUtils';
import { encodeURIPathname } from 'utils/String';

import BundleElement from './BundleElement';
import { type BundleElementSchema } from './BundleElement';
import type BundlesManager from './BundlesManager';
import DocumentCollection from './DocumentCollection';
import { DocumentSchema } from './DocumentSchema';
import DocumentStore from './DocumentStore';

export class BundleSchema extends DocumentSchema {
    adIds: Array<string> = [];
    compositeArchivePath: string = '';
    compositeArchivePathsByType: Record<string, string> = {};
    createTime = nullTimestamp();
    creative: string = '';
    errors: Array<string> = [];
    initiatingUser: string = '';
    modifiedTime = nullTimestamp();
    mutex = 0;
    name: string = '';
    pendingCount = 0;
    stagePrefix: string = '';
    status: ProcessingStatus = ProcessingStatus.NONE;
    archived: boolean = false;
    taskId: string = '';
}

export default class Bundle extends DocumentStore<BundleSchema> {
    declare parent: BundlesManager | undefined;

    type = DocumentType.BUNDLE;
    collectionType = DocumentCollectionType.BUNDLE;
    schema = new BundleSchema();

    elements = new DocumentCollection<BundleElement, BundleElementSchema>(
        this,
        BundleElement
    );
    warning = false;

    baseURL = getHostingBucket().websiteOrigin;

    constructor(parent: BundlesManager, docRef?: DocumentReference) {
        super(parent, docRef);
        this.becomeObservable();
        this.elements.becomeObservable();
    }

    get loading() {
        return this.data.status !== ProcessingStatus.COMPLETE;
    }

    get downloadURL() {
        // compositeArchivePath predates the dist/src split, and is present
        // on older bundle objects
        return this.storagePathToURL(
            this.data.compositeArchivePathsByType?.dist ||
                this.data.compositeArchivePath
        );
    }

    get sourceURL() {
        return this.storagePathToURL(
            this.data.compositeArchivePathsByType?.source
        );
    }

    private storagePathToURL(storagePath: string | null | undefined) {
        if (storagePath == null) return '';
        const url = new URL(encodeURIPathname(storagePath), this.baseURL);
        return String(url);
    }

    get formattedDateModified() {
        return formatDateTimeLongMinutes(this.data.modifiedTime);
    }

    public getAllStoragePaths() {
        const data = this.data;
        return [
            data.compositeArchivePath,
            ...Object.values(data.compositeArchivePathsByType),
        ].filter(Boolean);
    }

    // FIXME dormant: nothing calls this, so `warning` is never set and
    // BundleWarningIcon's oversize case can't appear (its ERROR case still
    // works). This was the bundle-level summary of the per-element size check
    // that BundleElementRow still does in the Details modal. It needs every
    // element loaded, and the calls that did that eagerly (from onReady,
    // onUpdate and a status reaction) were all commented out in 1878e923
    // ("initial steps at fixing memory and performance issues") - elements
    // are now loaded on demand when Details is opened. Reviving it means
    // choosing when to pay for that load. Note that it also duplicates
    // BundleElementRow's size arithmetic, odd 1024/1000 divisors included.
    checkElementsForIssues(elements: Array<BundleElement>) {
        console.log(`Bundle ${this.data.name}: Checking for issues...`);
        if (!elements) return;
        for (const element of elements) {
            const distData = element.data.builds?.find(
                (b) => b.buildType === 'dist'
            );
            if (!distData) continue;

            const zipSize = distData.archiveActualSize / 1024;
            const zipMaxSize = distData.archiveMaxSize / 1000;
            const zipOverSize = zipSize > zipMaxSize;

            const imageSize = element.data.lossyScreenshotActualSize / 1024;
            const imageMaxSize = element.data.lossyScreenshotMaxSize / 1000;
            const imageOverSize = imageSize > imageMaxSize;

            if (zipOverSize || imageOverSize) {
                this.warning = true;
            }
        }
    }
}
